import { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import { hdlChg } from "./PreviewForm";
import { catalogByPart } from "../../../apis";
import { isForLanding } from "../consts";
import "./FormInputSurvey.scss";

// Importar los SVG directamente para preservar sus colores internos
import { ReactComponent as FeedbackVeryPoor } from "../../../assets/icons/feedback-verypoor.svg";
import { ReactComponent as FeedbackPoor } from "../../../assets/icons/feedback-poor.svg";
import { ReactComponent as FeedbackNeutral } from "../../../assets/icons/feedback-neutral.svg";
import { ReactComponent as FeedbackGood } from "../../../assets/icons/feedback-good.svg";
import { ReactComponent as FeedbackExcellent } from "../../../assets/icons/feedback-excellent.svg";

// Componente que retorna un mensaje de error en caso de que el input sea invalido
const ReturnErrorMesages = ({ tryNext, valid }) => {
  return (
    <>
      {tryNext &&
        Array.isArray(valid) &&
        valid?.length > 0 &&
        valid.map((err, idx) => (
          <div key={idx} className="invalid-msg">
            {err} <br />
          </div>
        ))}
    </>
  );
};

// Limpia el prefijo numérico del label (ej: "1 Texto" -> "Texto", "01. Texto" -> "Texto")
const cleanLabelPrefix = (label) => {
  if (!label) return label;
  return label.replace(/^\d+[\s.\-_]+/, '');
};

// Mapeo de keys del catálogo a componentes SVG
const SURVEY_ICON_MAP = {
  "idont_trust": FeedbackVeryPoor,
  "ipartially_trust": FeedbackPoor,
  "neutral": FeedbackNeutral,
  "itrust": FeedbackGood,
  "ifully_trust": FeedbackExcellent,
};

// Orden de las keys del catálogo
const ORDERED_KEYS = [
  "idont_trust",
  "ipartially_trust",
  "neutral",
  "itrust",
  "ifully_trust"
];

// Input de Survey con iconos de feedback
const FormInputSurvey = ({
  schema,
  origin,
  value,
  valid,
  br,
  tryNext,
  handleChange,
  handleValidate,
  entireFormData,
  entirePathData,
  setFormData,
  lang: forcedLang
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [catalogueOptions, setCatalogueOptions] = useState([]);

  // Obtener el idioma de la misma manera que CatalogueInput
  const lang = forcedLang ? forcedLang : isForLanding ? localStorage.getItem("selectedLanguage") : localStorage.getItem("lang") ?? "es";

  // Cargar las opciones del catálogo con el idioma correcto
  useEffect(() => {
    if (schema.catalogue) {
      catalogByPart(
        { is_own: schema.isOwn, catalogue: schema.catalogue, path: "/" },
        lang
      )
        .then((resp) => {
          if (resp?.data && Array.isArray(resp.data)) {
            setCatalogueOptions(resp.data);
          }
        })
        .catch(console.log);
    }
  }, [schema.catalogue, schema.isOwn, lang]);

  // Sincronizar el estado local con el valor del formulario
  useEffect(() => {
    if (value) {
      // El valor viene en formato "type::key", extraer solo la key
      const keyParts = value.split("::");
      const actualKey = keyParts[keyParts.length - 1];
      setSelectedValue(actualKey);
    }
  }, [value]);

  // Manejar la selección de un icono
  const handleIconClick = (optionKey) => {
    setSelectedValue(optionKey);

    // Emitir el cambio usando el formato de catálogos: "type::key"
    const catalogValue = `type::${optionKey}`;

    // Preparar params como lo hace CatalogueInput
    const params = {
      catalogue: schema.catalogue,
      sensitive: schema.sensitive,
      isOwn: schema.isOwn,
      origin,
    };

    // Usar hdlChg para guardar correctamente en la estructura del formulario
    hdlChg({
      e: {
        target: {
          name: schema.catalogue,
          value: catalogValue,
        },
      },
      entirePathData,
      params,
      origin,
      entireFormData,
      setFormData,
    });

    // Validar que el campo tiene valor (ya no es obligatorio vacío)
    if (handleValidate) {
      handleValidate(true);
    }
  };

  // Obtener la etiqueta traducida de una opción del catálogo
  const getOptionLabel = (key) => {
    const option = catalogueOptions.find((opt) => opt.key === key);
    if (option) {
      // Usar locales[lang] si existe, de lo contrario usar label
      return option.locales?.[lang] || option.label || key;
    }
    return key;
  };

  // Ordenar las opciones según el orden deseado y filtrar solo las activas
  const getOrderedOptions = () => {
    return ORDERED_KEYS
      .filter((key) => {
        // Verificar que existe el icono para esta key
        if (!SURVEY_ICON_MAP[key]) return false;
        // Si hay opciones del catálogo, verificar que la opción esté activa
        if (catalogueOptions.length > 0) {
          const option = catalogueOptions.find((opt) => opt.key === key);
          return option?.is_active !== false;
        }
        return true;
      });
  };

  return (
    <>
      <Col
        lg={schema.grid || 12}
        className="preview-input-container form-input-survey"
      >
        <label className={`${schema.required ? "label-required" : ""}`}>
          {schema.locales?.[lang] === undefined
            ? schema.label
            : schema.locales[lang]}
          {schema.required ? " *" : ""}
        </label>

        <div className={`survey-icons-container ${selectedValue ? "has-selection" : ""}`}>
          {getOrderedOptions().map((key) => {
            const IconComponent = SURVEY_ICON_MAP[key];
            const isSelected = selectedValue === key;

            // Obtener el label traducido del catálogo y limpiar el prefijo numérico
            const label = getOptionLabel(key);
            const cleanedLabel = cleanLabelPrefix(label);

            return (
              <div
                key={key}
                className={`survey-icon-wrapper ${isSelected ? "selected" : ""}`}
                onClick={() => handleIconClick(key)}
                title={cleanedLabel}
              >
                <span className="survey-feedback-icon-wrapper">
                  <IconComponent className="survey-feedback-icon" />
                </span>
                <span className="survey-icon-label">{cleanedLabel}</span>
              </div>
            );
          })}
        </div>

        <ReturnErrorMesages tryNext={tryNext} valid={valid} />
      </Col>
      {br && <div></div>}
    </>
  );
};

export { FormInputSurvey };
